// 09-REVIEW CR-05 (#408) — the module's ONE place for calendar-date
// handling. Two wire shapes cross the Service API and they must never share
// a formatter:
//
//   * DATE columns (`due_date`) — a calendar day the server already resolved
//     dealer-locally (D8-03 via `DealerDayClock`), emitted as a midnight-UTC
//     instant (`2026-09-07T00:00:00.000000Z`). Rendered AS-IS through
//     `formatCalendarDate`: any zone conversion shows a dealer west of UTC
//     the previous day, and the on-screen due date then disagrees with the
//     invoice PDF (which prints the `Y-m-d`).
//
//   * timestamp columns (`dropoff_date`, `promised_date`, `processed_at`) —
//     real instants. The server stores a bare `YYYY-MM-DD` input as
//     DEALER-LOCAL midnight (`DealerDayClock::dateInputToInstant`), so they
//     are rendered and pre-filled THROUGH the dealer's zone via
//     `formatDealerDate` / `toDealerLocalDateInput` — never `slice(0, 10)`,
//     which is wrong for every dealer east of UTC.
//
// The dealer zone is `dealershipProfile.timezone` (the same source the
// list page and the create Sheet already read); the browser zone is only
// the fallback while the profile is still loading.

const MONTH_DAY_YEAR: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };

function withTimeZone(
  options: Intl.DateTimeFormatOptions,
  timezone?: string | null,
): Intl.DateTimeFormatOptions {
  return timezone ? { ...options, timeZone: timezone } : options;
}

/**
 * Today's calendar date IN THE DEALER'S LOCAL DAY (the D6-04 `DealerDayClock`
 * posture carried into the client), never the UTC date. A dealership west of
 * UTC can be a full calendar day behind at the moment of drop-off; one east
 * of UTC a day ahead. Falls back to the browser zone when the profile is not
 * loaded yet. `en-CA` formats as `YYYY-MM-DD`.
 */
export function todayLocalDate(timezone?: string | null): string {
  try {
    return new Intl.DateTimeFormat('en-CA', withTimeZone({}, timezone)).format(new Date());
  } catch {
    return new Intl.DateTimeFormat('en-CA').format(new Date());
  }
}

/**
 * A DATE value rendered as-is — never through a zone conversion. Accepts
 * both a bare `YYYY-MM-DD` and the midnight-UTC instant a `date`-cast
 * column serialises to; only the first ten characters are ever read.
 */
export function formatCalendarDate(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return '—';
  const [, year, month, day] = match;
  return new Intl.DateTimeFormat('en-US', { ...MONTH_DAY_YEAR, timeZone: 'UTC' }).format(
    new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))),
  );
}

/** An instant rendered on the dealer's calendar (browser zone until the profile loads). */
export function formatDealerDate(value: string | null | undefined, timezone?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', withTimeZone(MONTH_DAY_YEAR, timezone)).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', MONTH_DAY_YEAR).format(date);
  }
}

const MONTH_DAY_YEAR_TIME: Intl.DateTimeFormatOptions = {
  ...MONTH_DAY_YEAR,
  hour: 'numeric',
  minute: '2-digit',
};

/**
 * D17-05 — an instant as the dealer-local date AND time ("Oct 17, 2026, 3:00 PM"),
 * so two same-day re-captures (D17-08) stay distinguishable. Same zone rules as
 * `formatDealerDate`.
 */
export function formatDealerDateTime(value: string | null | undefined, timezone?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  try {
    return new Intl.DateTimeFormat('en-US', withTimeZone(MONTH_DAY_YEAR_TIME, timezone)).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', MONTH_DAY_YEAR_TIME).format(date);
  }
}

/**
 * An instant as the dealer-local `YYYY-MM-DD` an `<input type="date">`
 * expects — the edit-form prefill for the timestamp columns.
 */
export function toDealerLocalDateInput(
  value: string | null | undefined,
  timezone?: string | null,
): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('en-CA', withTimeZone({}, timezone)).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-CA').format(date);
  }
}
